import { BMBadgeTag, BMText } from "@traveloka/district-bloom";
import IcTripOneWay from "@traveloka/icon-kit-web/react/IcTripOneWay";
import IcTripRoundTrip from "@traveloka/icon-kit-web/react/IcTripRoundTrip";
import { differenceInDays } from "date-fns";
import { Image, StyleSheet } from "react-native";
import { NO_TRIP_IMAGE } from "../../../../assets/next-trip/no-trip";
import { DotLoader } from "../../../../components";
import BMView from "../../../../components/View/BMView";
import { useLocalizedDateFormat } from "../../../../core";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { formatMessage } from "../../../../utils";
import {
  MobileHomeNextTripProps,
  NextTripTabsType,
} from "./MobileHomeNextTrip";

type Props = MobileHomeNextTripProps & {
  selectedTab: NextTripTabsType;
};

export default function NextTripContent(props: Props) {
  const { data, selectedTab, redirectToBookingDetail } = props;

  const {
    CorporateBookingStatusTextSwitcher: crStatus,
    CorporatePostLogin: cr,
  } = useMobileContentResource();
  const { format } = useLocalizedDateFormat();

  const selectedData = data[selectedTab];
  const { isLoading, isError, reFetchData, redirectToSearchForm } =
    selectedData;

  if (isLoading) {
    return (
      <BMView flex justify="center" align="center">
        <DotLoader />
      </BMView>
    );
  }

  if (isError) {
    return (
      <BMView flex justify="center" align="center" spacing="sm">
        <BMText variant="paragraphSmall" ink="BMStyleTextInkSecondary">
          {cr.errorText}
        </BMText>
        <BMText
          style={styles.forceRight}
          variant="uiNormal"
          ink="BMStyleTextInkAction"
          onPress={reFetchData}
        >
          {cr.errorCta}
        </BMText>
      </BMView>
    );
  }

  const isEmpty = selectedData.productData === null;

  if (isEmpty) {
    return (
      <BMView row flex justify="center" align="center" spacing="lg">
        <Image style={styles.noTripImage} source={{ uri: NO_TRIP_IMAGE }} />
        <BMView flex spacing="sm" align="center">
          <BMText variant="paragraphSmall" ink="BMStyleTextInkSecondary">
            {cr.noTripText}
          </BMText>
          <BMText
            style={styles.forceRight}
            variant="uiNormal"
            ink="BMStyleTextInkAction"
            onPress={redirectToSearchForm}
          >
            {cr.bookFlightCta}
          </BMText>
        </BMView>
      </BMView>
    );
  }

  if (selectedTab === "FLIGHT") {
    // Render flight data
    const {
      arrivalLocation,
      bookingId,
      bookingStatus,
      departureDate,
      departureLocation,
      isRoundTrip,
      numOfPassenger,
    } = data[selectedTab].productData!;

    return (
      <BMView flex justify="between">
        <BMView flex spacing="xs">
          <BMView row spacing="xs" align="center">
            <BMText>{departureLocation}</BMText>
            {isRoundTrip ? (
              <IcTripRoundTrip
                width={16}
                height={16}
                color="#000"
                accentColor="#000"
              />
            ) : (
              <IcTripOneWay
                width={16}
                height={16}
                color="#000"
                accentColor="#000"
              />
            )}
            <BMText>{arrivalLocation}</BMText>
          </BMView>
          <BMText variant="uiSmallLi" ink="BMStyleTextInkSecondary">
            {formatMessage(cr.passengerUnit, { num: numOfPassenger })} ·{" "}
            {format(departureDate, "SHORT_MONTH")}
          </BMText>
        </BMView>
        <BMView row justify="between" align="center">
          <BMBadgeTag label={crStatus[bookingStatus]} type="GREEN" />
          <BMText
            ink="BMStyleTextInkAction"
            onPress={() => redirectToBookingDetail("FLIGHT", bookingId)}
          >
            {cr.seeDetailCta}
          </BMText>
        </BMView>
      </BMView>
    );
  }

  if (selectedTab === "HOTEL") {
    const {
      bookingId,
      bookingStatus,
      checkInDate,
      checkOutDate,
      hotelName,
      numOfGuest,
      numOfRoom,
    } = data[selectedTab].productData!;
    return (
      <BMView flex justify="between">
        <BMView spacing="xs">
          <BMText>{hotelName}</BMText>
          <BMText variant="uiSmallLi" ink="BMStyleTextInkSecondary">
            {formatMessage(cr.guestUnit, { num: numOfGuest })} ·{" "}
            {format(checkInDate, "SHORT_MONTH")} ·{" "}
            {formatMessage(cr.nightUnit, {
              num: differenceInDays(checkOutDate, checkInDate),
            })}{" "}
            · {formatMessage(cr.roomUnit, { num: numOfRoom })}
          </BMText>
        </BMView>
        <BMView row justify="between" align="center">
          <BMBadgeTag label={crStatus[bookingStatus]} type="GREEN" />
          <BMText
            ink="BMStyleTextInkAction"
            onPress={() => redirectToBookingDetail("HOTEL", bookingId)}
          >
            {cr.seeDetailCta}
          </BMText>
        </BMView>
      </BMView>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  noTripImage: {
    width: 59,
    height: 52.5,
  },
  forceRight: {
    marginLeft: "auto",
  },
});
